import { debounceTime } from 'rxjs/operators'
import { SearchFinancialInstitutionRequest } from './../../../shared/shared.actions'
import { getSearchFinancialInstitutionsState } from './../../../shared/shared.select'
import { ActivatedRoute } from '@angular/router'
import { getRoles, getUpdateUserState } from './../users.selectors'
import { Store, select } from '@ngrx/store'
import {
  UpdateField,
  FetchRolesRequest,
  SetDefaultData,
  FetchUserRequest,
  UpdateUserRequest,
  UpdateUserPasswordRequest,
} from './../users.actions'
import Validator from '@shared/validatorjs/src/validator'
import { Component, OnInit, OnDestroy } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription, Subject } from 'rxjs'
import { Map } from 'immutable'

@Component({
  selector: 'app-update-user',
  templateUrl: './update-user.component.html',
  styleUrls: ['./update-user.component.css'],
})
export class UpdateUserComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  public roles: any[]
  private rolesState$: Subscription
  private state$: Subscription
  public isLoading: boolean
  public isPasswordUpdating: boolean
  public userId: number
  public financialInstitutions: any[]
  public onFinancialInstitutionAutocomplete$ = new Subject<string>()
  private financialInstitutionAutocomplete$: Subscription
  private searchFinancialInstitutionState$: Subscription

  constructor(public store: Store<any>, private route: ActivatedRoute) {
    super()
  }

  ngOnInit() {
    this.userId = parseInt(this.route.snapshot.paramMap.get('id'), 10)
    this.store.dispatch<any>(new SetDefaultData('updateUser'))

    this.rolesState$ = this.store.pipe(select(getRoles)).subscribe((roles) => {
      this.roles = roles.toJS()
    })

    this.searchFinancialInstitutionState$ = this.store
      .pipe(select(getSearchFinancialInstitutionsState))
      .subscribe((financialInstitutions) => {
        this.financialInstitutions = financialInstitutions.get('data').toJS()
      })

    this.state$ = this.store
      .pipe(select(getUpdateUserState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
        this.isPasswordUpdating = state.get('isPasswordUpdating')
      })

    this.financialInstitutionAutocomplete$ =
      this.onFinancialInstitutionAutocomplete$
        .pipe(debounceTime(500))
        .subscribe((keyword: string | null) => {
          this.store.dispatch<any>(
            new SearchFinancialInstitutionRequest(keyword ? keyword : ''),
          )
        })

    this.store.dispatch<any>(new FetchUserRequest(this.userId, 'updateUser'))
    this.store.dispatch<any>(new FetchRolesRequest())
    this.store.dispatch<any>(new SearchFinancialInstitutionRequest(''))
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.rolesState$.unsubscribe()
    this.searchFinancialInstitutionState$.unsubscribe()
    this.financialInstitutionAutocomplete$.unsubscribe()
  }

  public checkboxChange(e: any, name?: string): void {
    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        e.target.checked,
        'updateUser.data',
      ),
    )
  }

  public handleFieldChange(e: any, name: null | string = null): void {
    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name ? e : e.target.value,
        'updateUser.data',
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        'updateUser',
      ),
    )
  }

  validation() {
    const data = this.state.get('data')
    const rules = {
      firstName: 'required',
      lastName: 'required',
      roles: 'required|array',
    }
    const validator = new Validator(
      {
        firstName: data.get('firstName'),
        lastName: data.get('lastName'),
        roles: data.get('roles') ? data.get('roles').toJS() : [],
      },
      rules,
    )

    validator.setAttributeNames({
      firstName: 'first name',
      lastName: 'last name',
    })

    return validator
  }

  public updatePassword() {
    const data = this.state.get('data')
    const rules = {
      password: 'required',
      confirm_password: 'required|same:password',
    }
    const validator = new Validator(
      {
        password: data.get('password'),
        confirm_password: data.get('confirm_password'),
      },
      rules,
    )

    if (validator.passes()) {
      this.store.dispatch(
        new UpdateUserPasswordRequest(this.userId, {
          password: data.get('password'),
        }),
      )
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validator.errors.errors,
          'updateUser',
        ),
      )
    }
  }

  public onSubmit(e: Event): void {
    e.preventDefault()

    const validation = this.validation()
    const payload = this.state
      .get('data')
      .set(
        'roles',
        (this.state.getIn(['data', 'roles']) as any[]).map((r: any) =>
          r.get('name'),
        ),
      )
      .toJS()

    if (validation.passes()) {
      this.store.dispatch(new UpdateUserRequest(this.userId, payload))
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validation.errors.errors,
          'updateUser',
        ),
      )
    }
  }
}
